import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect } from "react";

import { gql, useQuery } from "@apollo/client";
import clsx from "clsx";
import moment from "moment";
import { useTranslation } from "react-i18next";
import logo from "../../assets/Image/logo.png";
import NotFound from "../../Error/NotFound";
import FullScreenLoading from "../HOC/FunctionComponents/LoadingPages/FullScreenLoading";

import * as gqlb from "gql-query-builder";
import { FixedTableCell } from "../HOC/CustomComponents/FixedTableCell";
import { urlParameters } from "../HOC/CustomFunctions/urlParameters";
import "../ManifestPrint/print.css";

const PREFIX = "InvoicePrint";

const classes = {
  headerCells: `${PREFIX}-headerCells`,
  title: `${PREFIX}-title`,
  notes: `${PREFIX}-notes`,
  noteCell: `${PREFIX}-noteCell`,
  barcodeFont: `${PREFIX}-barcodeFont`,
  cairoFont: `${PREFIX}-cairoFont`,
  barcode: `${PREFIX}-barcode`,
  headerTableCell: `${PREFIX}-headerTableCell`,
  mainCellBorder: `${PREFIX}-mainCellBorder`,
  signature: `${PREFIX}-signature`,
  reportHeader: `${PREFIX}-reportHeader`,
  hiddenFooter: `${PREFIX}-hiddenFooter`,
  pageFooter: `${PREFIX}-pageFooter`,
  logoSection: `${PREFIX}-logoSection`,
  headerDate: `${PREFIX}-headerDate`,
  whiteSpace: `${PREFIX}-whiteSpace`,
  approve: `${PREFIX}-approve`,
  rowWithoutBorder: `${PREFIX}-rowWithoutBorder`,
  mainDetailsCell: `${PREFIX}-mainDetailsCell`,
  tableHeadCell: `${PREFIX}-tableHeadCell`,
  tableWidth: `${PREFIX}-tableWidth`,
  shipmentsTable: `${PREFIX}-shipmentsTable`,
  signatureBlock: `${PREFIX}-signatureBlock`,
};

const Root = styled("div")(({ theme }) => ({
  fontFamily: ['"Cairo"', "sans-serif"].join(","),

  [`& .${classes.headerCells}`]: {
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.title}`]: {
    color: theme.palette.info.main,
    // whiteS;pace: "break-spaces",
  },

  [`& .${classes.notes}`]: {
    padding: theme.spacing(1),
    border: "1px solid " + theme.palette.action.disabled,
    wordBreak: "break-all",
    width: "100%",
    minHeight: 80,
  },

  [`& .${classes.noteCell}`]: {
    width: "30%",
    overflow: "hidden",
  },

  [`& .${classes.barcodeFont}`]: {
    fontFamily: '"Libre Barcode 39 Text"',
  },

  [`& .${classes.cairoFont}`]: {
    fontFamily: ['"Cairo"', "sans-serif"].join(","),
  },

  [`& .${classes.barcode}`]: {
    fontSize: 40,
    textAlign: "center",
  },

  [`& .${classes.headerTableCell}`]: {
    border: "none",
    padding: 0,
  },

  [`& .${classes.mainCellBorder}`]: {
    border: "none",
    padding: 0,
  },

  [`& .${classes.signature}`]: {
    textAlign: "center",
    padding: theme.spacing(2, 1, 1, 1),
    border: "none",
  },

  [`& .${classes.reportHeader}`]: {
    whiteSpace: "pre-line",
    padding: theme.spacing(0, 2),
    lineHeight: "120%",
    fontSize: 12,
  },

  [`& .${classes.hiddenFooter}`]: {
    visibility: "hidden",
  },

  [`& .${classes.pageFooter}`]: {
    //   "&::after": {
    //    " content:  counter(page)  of  counter(pages)"
    //   },
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    borderTop: "1px solid " + theme.palette.text.secondary,
    display: "flex",
    alignItems: "flex-end",
  },

  [`& .${classes.logoSection}`]: {
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.headerDate}`]: {
    textAlign: "end",
  },

  [`& .${classes.whiteSpace}`]: {
    "& tr": {
      "& td": {
        whiteSpace: "initial",
      },
    },
  },

  [`& .${classes.approve}`]: {
    textAlign: "end",
  },

  [`& .${classes.rowWithoutBorder}`]: {
    "& td": {
      border: "none",
      padding: 0,
    },
  },

  [`& .${classes.mainDetailsCell}`]: {
    "& td": { border: "none", verticalAlign: "top" },
    "& td:first-of-type": { paddingLeft: 0 },
    "& td:last-of-type": { paddingRight: 0 },

    border: "none",
    padding: theme.spacing(1, 0),
  },

  [`& .${classes.tableHeadCell}`]: {
    padding: 0,
  },

  [`& .${classes.tableWidth}`]: {
    width: "100vw",
  },

  [`& .${classes.shipmentsTable}`]: {
    "& tr": {
      "&:hover": {
        backgroundColor: "#ffffff00 !important",
      },
    },
    "& td , & th": {
      padding: theme.spacing(1),
    },
  },

  [`& .${classes.signatureBlock}`]: {
    breakInside: "avoid",
  },
}));

export {};

const org = {
  operation: "organization",
  fields: [
    "name",
    "phone",
    "email",
    "website",
    "reportHeader",
    "reportFooter",
    "registrationNumber",
    "taxNumber",
  ],
  variables: {},
};

const invoicesSum = {
  operation: "sumInvoices",
  fields: [
    "shipmentsCount",
    "piecesCount",
    "weight",
    "deliveryFees",
    "postFees",
    "tax",
    "allFees",
  ],
  variables: {
    input: {
      type: "ListInvoicesFilterInput",
      required: true,
    },
  },
};
const invoicesListFields = [
  {
    data: [
      "code",
      "id",
      "date",
      "approved",
      "glApproved",
      "weight",
      "pieces",
      "shipmentsCount",
      "deliveryFees",
      "postFees",
      "tax",
      "allFees",
      "notes",
      {
        operation: "transactionType",
        fields: ["id", "name"],
        variables: {},
      },

      {
        customer: ["id", "name", "code"],
      },
    ],
  },
];

const PAGE_COUNT = gqlb.query({
  operation: "listInvoices",
  fields: [{ paginatorInfo: ["lastPage"] }],
  variables: {
    input: {
      type: "ListInvoicesFilterInput",
      required: true,
    },
    first: {
      type: "Int",
    },
  },
});

const InvoicePrint = (props) => {
  const urlQuery = urlParameters(window.location.search);

  const stringValues = ["code", "fromCode", "toCode"];
  const floatValues = ["fromWeight", "toWeight"];
  stringValues.forEach((i) => {
    if (urlQuery[i]) urlQuery[i] = String(urlQuery[i]);
  });
  console.log(urlQuery);
  floatValues.forEach((i) => {
    if (urlQuery[i]) urlQuery[i] = parseFloat(urlQuery[i]);
  });
  console.log(urlQuery);

  const { data: pageCount, loading: pageLoad } = useQuery(
    gql`
      ${PAGE_COUNT.query}
    `,
    {
      first: 100,
      variables: {
        input: urlQuery,
        first: 100,
      },
    }
  );
  const pages = pageCount?.listInvoices?.paginatorInfo?.lastPage;

  const queryBuilder = () => {
    const queryNumber = Math.ceil(pages / 100);
    const queryArray = [org, invoicesSum];
    for (let index = 0; index < queryNumber; index++) {
      const initialQuery = {
        operation: `listInvoices${index}:listInvoices`,
        fields: invoicesListFields,
        variables: {
          input: {
            type: "ListInvoicesFilterInput",
            required: true,
          },
          first: {
            type: "Int",
          },
          ["page" + index]: {
            type: "Int",
            name: "page",
          },
        },
      };
      queryArray.push(initialQuery);
    }
    return queryArray;
  };

  const queryBody = queryBuilder();
  const INVOICES_PRINT = gqlb.query(queryBody);

  const { t } = useTranslation();

  let variables = {
    input: urlQuery,
    first: 100,
  };
  for (let index = 0; index < pages; index++) {
    variables["page" + index] = index + 1;
  }

  const { data: queryData, loading } = useQuery(
    gql`
      ${INVOICES_PRINT.query}
    `,
    {
      fetchPolicy: "no-cache",
      nextFetchPolicy: "no-cache",
      skip: !pages,
      variables,
      onError: () => {
        return;
      },
    }
  );

  const reportDetails =
    queryData?.listInvoices?.data !== null
      ? queryData?.listInvoices?.data
      : null;

  let parsedData = [];
  if (queryData?.listInvoices0) {
    let concatData = [];
    for (const key in queryData) {
      if (key.startsWith("list")) {
        const listData = queryData[key].data;
        concatData = concatData.concat(listData);
      }
    }
    parsedData = concatData;
  }

  useEffect(() => {
    if (parsedData.length > 0) {
      window.print();
    }
    return () => {};
  }, [queryData]);

  const sumInvoices =
    queryData?.sumInvoices !== null ? queryData?.sumInvoices : null;

  const header = !loading && !pageLoad && reportDetails !== null && (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell padding="none">
            <Table>
              <TableBody>
                <TableRow className={classes.rowWithoutBorder}>
                  <TableCell>
                    <Box className={classes.logoSection}>
                      <img src={logo} alt="logo" height={30} />
                      <Typography
                        className={clsx(classes.reportHeader, "landscape-zoom")}
                      >
                        {queryData.organization.reportHeader}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell className={clsx(classes.headerDate)}>
                    <Typography variant="h6">
                      {moment(new Date()).format("ll")}
                    </Typography>
                    <TypographyKeyValue
                      title={t("registrationNumber")}
                      value={queryData.organization?.registrationNumber}
                      hidden={!queryData.organization?.registrationNumber}
                    />
                    <TypographyKeyValue
                      title={t("taxNumber")}
                      value={queryData.organization?.taxNumber}
                      hidden={!queryData.organization?.taxNumber}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell className={classes.headerTableCell}>
                    <Table className={classes.whiteSpace}>
                      <TableBody>
                        <TableRow className={classes.rowWithoutBorder}>
                          <TableCell>
                            <Typography variant="h5">
                              {t("invoiceList")}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TypographyKeyValue
                              title={t("fromDate")}
                              value={urlQuery?.fromDate && moment(urlQuery?.fromDate).format("ll")}
                            />
                            <TypographyKeyValue
                              title={t("toDate")}
                              value={urlQuery?.toDate && moment(urlQuery?.toDate).format("ll")}
                            />
                          </TableCell>
                          <TableCell>
                            <TypographyKeyValue
                              title={t("fromWeight")}
                              value={urlQuery?.fromWeight}
                            />
                            <TypographyKeyValue
                              title={t("toWeight")}
                              value={urlQuery?.toWeight}
                            />
                            {/* <TypographyKeyValue
                              title={t("customer")}
                              value={restParams}
                            /> */}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
  const table = !loading && !pageLoad && reportDetails !== null && (
    <Table id="manifestPrintTable">
      <EnhancedTableHead />

      <TableBody>
        {parsedData.length > 0 &&
          parsedData?.map((row, index) => {
            return (
              <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={index}
                // onClick={(e) => {
                //   e.stopPropagation();
                //   pushUrl(props, `/admin/invoices/${row?.id}/edit`);
                // }}
              >
                <FixedTableCell
                  pathname={`/admin/invoices/${row?.id}`}
                  component="th"
                  scope="row"
                >
                  {row.code}
                </FixedTableCell>
                <FixedTableCell>
                  {moment(row?.date).format("ll")}
                </FixedTableCell>

                <FixedTableCell>
                  <Box
                    component="span"
                    fontWeight="bold"
                  >{` (${row.customer.code}) `}</Box>
                  <Box component="span">{row.customer.name}</Box>
                </FixedTableCell>

                <FixedTableCell>{row?.shipmentsCount}</FixedTableCell>
                <FixedTableCell>{row?.pieces}</FixedTableCell>
                <FixedTableCell>{row?.weight}</FixedTableCell>
                <FixedTableCell>{row?.deliveryFees}</FixedTableCell>
                <FixedTableCell>{row?.postFees}</FixedTableCell>
                <FixedTableCell>{row?.tax}</FixedTableCell>
                <FixedTableCell>{row?.allFees}</FixedTableCell>
              </TableRow>
            );
          })}
        <TableRow>
          <FixedTableCell colSpan={2} />
          <FixedTableCell variant="head">{t("total")}</FixedTableCell>
          <FixedTableCell variant="head">
            {sumInvoices?.shipmentsCount}
          </FixedTableCell>
          <FixedTableCell variant="head">
            {sumInvoices?.piecesCount}
          </FixedTableCell>
          <FixedTableCell variant="head">{sumInvoices?.weight}</FixedTableCell>
          <FixedTableCell variant="head">
            {sumInvoices?.deliveryFees}
          </FixedTableCell>
          <FixedTableCell variant="head">
            {sumInvoices?.postFees}
          </FixedTableCell>
          <FixedTableCell variant="head">{sumInvoices?.tax}</FixedTableCell>
          <FixedTableCell variant="head">{sumInvoices?.allFees}</FixedTableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  return (
    <Root>
      {loading || pageLoad ? (
        <>
          <FullScreenLoading minHeight="20%" />
          {/* this spans for download font before print */}
          <span className={clsx(classes.barcodeFont)} />
          <span style={{ opacity: 0 }} className={classes.cairoFont}>
            lا
          </span>
        </>
      ) : reportDetails === null ? (
        <NotFound />
      ) : (
        <>
          <div>
            <div className="page-header">{header}</div>

            <div className="page-footer">
              {queryData.organization.reportFooter}
            </div>

            <table style={{ width: "100%" }}>
              <thead>
                <tr>
                  <td>
                    <div className="page-header-space">{header}</div>
                  </td>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>
                    <div
                      className={clsx(classes.shipmentsTable, {
                        // [classes.tableWidth]: tableZoom,
                      })}
                    >
                      {table}
                    </div>
                  </td>
                </tr>
              </tbody>

              <tfoot>
                <tr>
                  <td>
                    <div className="page-footer-space">
                      {queryData.organization.reportFooter}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </Root>
  );
};

export default InvoicePrint;

function TypographyKeyValue(props) {
  const { title, value, hidden, ...restProps } = props;
  return hidden ? null : (
    <Typography variant="body1" paddingBottom={1} {...restProps}>
      <span className={classes.title}>{title}:</span> <span>{value}</span>
    </Typography>
  );
}

function EnhancedTableHead() {
  //*********Table Function*********
  const { t } = useTranslation();
  const headCells = [
    { numeric: true, disablePadding: false, label: t("recordCode") },
    { numeric: true, disablePadding: false, label: t("date") },
    {
      numeric: true,
      disablePadding: false,
      label: t("customer"),
    },
    { numeric: true, disablePadding: false, label: t("shipmentsNumber") },
    { numeric: true, disablePadding: false, label: t("pieceCount") },
    { numeric: true, disablePadding: false, label: t("weight") },
    { numeric: true, disablePadding: false, label: t("deliveryFees") },
    { numeric: true, disablePadding: false, label: t("postFees") },
    { numeric: true, disablePadding: false, label: t("tax") },
    { numeric: true, disablePadding: false, label: t("value") },
  ];
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => {
          return (
            <FixedTableCell
              key={index}
              padding={headCell.disablePadding ? "none" : "normal"}
            >
              {headCell.label}
            </FixedTableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
